import { Stack, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { Loading } from "src/components/utils/Loading";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { usePackagingContext } from "./PackagingContext";
import { URLHandler } from "src/services/URLHandler.ts";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { ScoreService } from "src/services/ScoreService";
import { useEffect } from "react";
import { useGetPostScores } from "src/api/client-api/olapScores/useGetPostScores";
import { ScoreCards } from "src/components/ScoreCards/ScoreCards";
import { NoScoresAvailableMessage } from "src/components/Message/NoScoresAvailableMessage";
import { ScoreQueryError } from "src/components/ScoreState/ScoreQueryError";
import { getRemoteErrorStatus } from "src/api/remoteCollectionState";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

export const PackagingScores = () => {
    const theme = useTheme();
    const { company } = useCompanyMetadata();
    const {
        packaging,
        post,
        isPackagingLoading,
        isCampaignError,
        campaignError,
        retryCampaigns,
        isPostUnavailable,
        isPostIdle,
        isPostLoading,
        isPostSuccess,
        isPostError,
        postError,
        refetchPost,
    } = usePackagingContext();
    const { scoresWithVariations, lastSelectedVariation } = useEntityScores();
    const { isFeatureActivated } = useApplicationParameters();
    const isProductUxV2 = useIsProductUxV2();
    const appearance = isProductUxV2 ? "product-v2" : "legacy";
    const { scoreTypes } = useFoodpilotContext();

    const scoresParams = useOlapScoresParams({
        scores: {
            variations: ScoreService.getVariationByLastSelectedVariation(lastSelectedVariation),
        },
    });

    const {
        data: scores,
        error: scoreQueryError,
        isError: isScoreQueryError,
        isLoading: areScoresLoading,
        isRefetching: areScoresRefetching,
        refetch: retryScoreQuery,
    } = useGetPostScores({
        postId: post?.id,
        scoresQuery: scoresParams.query,
    });

    const navigate = useNavigate();
    const { packagingId } = useParams();

    useEffect(() => {
        scoresParams.state.setQuery({
            scores: {
                ...scoresParams.query.scores,
                variations: ScoreService.getVariationsByLastSelectedVariation(
                    scoresParams.query.scores?.variations,
                    lastSelectedVariation,
                ),
            },
        });
    }, [lastSelectedVariation.scoreId, lastSelectedVariation.variationId]);

    if (isPackagingLoading || isPostLoading || !packagingId || !scoreTypes || !packaging) {
        return <Loading appearance={appearance} />;
    }

    if (isCampaignError) {
        return (
            <ScoreQueryError context="details" onRetry={retryCampaigns} status={getRemoteErrorStatus(campaignError)} />
        );
    }

    if (isPostError) {
        return <ScoreQueryError context="details" onRetry={refetchPost} status={getRemoteErrorStatus(postError)} />;
    }

    if (isPostUnavailable) return <NoScoresAvailableMessage appearance={appearance} />;

    if (isPostIdle || !isPostSuccess) return <Loading appearance={appearance} />;

    if (!post) return <NoScoresAvailableMessage appearance={appearance} />;

    if (isScoreQueryError) {
        return (
            <ScoreQueryError
                context="details"
                onRetry={retryScoreQuery}
                status={getRemoteErrorStatus(scoreQueryError)}
            />
        );
    }

    if (!scoresWithVariations.length) return <NoScoresAvailableMessage appearance={appearance} />;

    if (scores && Object.keys(scores.olapScores).length === 0) {
        return <NoScoresAvailableMessage appearance={appearance} />;
    }

    const canDrilldown = isFeatureActivated("FS-packaging-score-details-distribution");

    return (
        <Stack gap={theme.spacing(2)}>
            <ScoreCards
                appearance={isProductUxV2 ? "product-v2" : "legacy"}
                scores={scores}
                isLoading={areScoresLoading || areScoresRefetching}
                showUnavailableWhenMissing
                showSpecificityRate={post?.isLocked}
                showDetails={canDrilldown}
                showDetailsAction={(scoreId: number) => {
                    navigate(URLHandler.packaging.score(company.id, Number(packagingId), scoreId));
                }}
            />
        </Stack>
    );
};
